import { Map } from 'immutable';
import throttle from 'lodash.throttle';
import { v4 as uuid } from 'uuid';

import {
  INIT_SUBSCRIBER,
  UPDATE_SUBSCRIBER,
  ERR_SUBSCRIBE,
  DESTROY_SUBSCRIBER,
} from 'OPENTOK/action_types';
import { SUBSCRIBER_STATUS_CONNECTED } from 'OPENTOK/models/subscriber';
import config from 'OPENTOK/config';

const initSubscribe = (id, sessionId, stream, object) => ({
  type: INIT_SUBSCRIBER,
  id,
  payload: {
    sessionId,
    object,
    streamId: stream.streamId,
    name: stream.name,
    videoType: stream.videoType,
    audioStatus: Map({ byPublisher: stream.hasAudio, bySubscriber: true }),
    videoStatus: Map({ byPublisher: stream.hasVideo, bySubscriber: true, byQuality: true }),
    audioVolume: object.getAudioVolume(),
  },
});

const updateSubscriberStatus = (id, status) => ({
  type: UPDATE_SUBSCRIBER,
  id,
  payload: {
    status,
  },
});

const subscriberVideoElementCreated = (id, videoElement) => ({
  type: UPDATE_SUBSCRIBER,
  id,
  payload: {
    videoElement,
  },
});

const destroySubscribe = (id) => ({
  type: DESTROY_SUBSCRIBER,
  id,
});

const errSubscribe = (err) => ({ type: ERR_SUBSCRIBE, err });

const subscriberVideoQualityChanged = (id, value) => ({
  type: UPDATE_SUBSCRIBER,
  id,
  payload: {
    videoStatus: Map({ byQuality: value }),
  },
});

const eventHandlers = (id, dispatch) => ({
  connected: () => {},
  disconnected: () => {},
  destroyed: () => {
    dispatch(destroySubscribe(id));
  },
  videoEnabled: (event) => {
    if (event.reason === 'quality') {
      dispatch(subscriberVideoQualityChanged(id, true));
    }
  },
  videoDisabled: (event) => {
    if (event.reason === 'quality') {
      dispatch(subscriberVideoQualityChanged(id, false));
    }
  },
  videoDisableWarning: () => {
    console.warn('subscriber videoDisableWarning');
  },
  videoDisableWarningLifted: () => {
    console.warn('subscriber videoDisableWarningLifted');
  },
  videoElementCreated: (event) => {
    dispatch(subscriberVideoElementCreated(id, event.element));
  },
});

export const subscribeStream = (sessionId, stream, propProperties = {}, targetElement = null) => (
  dispatch,
  getState,
) =>
  new Promise((resolve, reject) => {
    const subscriberId = uuid();
    const properties = {
      ...config.subscriber.default,
      ...propProperties,
    };

    const sessionObject = getState().getIn(['ot', 'sessions', sessionId, 'object']);
    const subscriber = sessionObject.subscribe(stream, targetElement, properties, (err) => {
      if (err) {
        dispatch(errSubscribe(err));
        reject(err);
      } else {
        dispatch(updateSubscriberStatus(subscriberId, SUBSCRIBER_STATUS_CONNECTED));
        resolve(subscriberId);
      }
    });

    subscriber.on(eventHandlers(subscriberId, dispatch));

    dispatch(initSubscribe(subscriberId, sessionId, stream, subscriber));
  });

export const toggleSubscriberMedia = (id, mediaType) => (dispatch, getState) => {
  const subscriber = getState().getIn(['ot', 'subscribers', id]);
  const subscriberObject = getState().getIn(['ot', 'subscribers', id, 'object']);
  if (!subscriber) {
    return;
  }

  let payload;
  if (mediaType === 'audio') {
    const value = !subscriber.getIn(['audioStatus', 'bySubscriber']);
    payload = { audioStatus: Map({ bySubscriber: value }) };
    subscriberObject.subscribeToAudio(value);

    // https://tokbox.com/developer/sdks/js/reference/OT.html#unblockAudio
    if (value == true && subscriberObject.isAudioBlocked()) {
      OT.unblockAudio();
    }

  } else if (mediaType === 'video') {
    const value = !subscriber.getIn(['videoStatus', 'bySubscriber']);
    payload = { videoStatus: Map({ bySubscriber: value }) };
    subscriberObject.subscribeToVideo(value);
  }

  dispatch({ type: UPDATE_SUBSCRIBER, id, payload });
};

export const updateSubscriberAudioVolume = (id, audioVolume) => (dispatch, getState) => {
  const subscriber = getState().getIn(['ot', 'subscribers', id]);
  const subscriberObject = getState().getIn(['ot', 'subscribers', id, 'object']);
  if (!subscriber) {
    return;
  }

  subscriberObject.setAudioVolume(audioVolume);

  dispatch({
    type: UPDATE_SUBSCRIBER,
    id,
    payload: {
      audioVolume: subscriberObject.getAudioVolume(),
    }
  });
};

export const forceDisconnectSubscriber = (id, sessionId) => (dispatch, getState) => {
  const sessionObject = getState().getIn(['ot', 'sessions', sessionId, 'object']);
  const subscriberObject = getState().getIn(['ot', 'subscribers', id, 'object']);
  
  sessionObject.forceDisconnect(subscriberObject.stream.connection);
  dispatch(destroySubscribe(id));
};